const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;
function assertDateOnly(value) {
    if (!dateOnlyPattern.test(value))
        throw new Error(`Invalid date-only value: ${value}`);
}
export function parseDateOnly(value) {
    assertDateOnly(value);
    const [year, month, day] = value.split("-").map(Number);
    return new Date(Date.UTC(year, month - 1, day));
}
export function formatDateOnly(date) {
    return date.toISOString().slice(0, 10);
}
export function addDays(dateOnly, days) {
    const date = parseDateOnly(dateOnly);
    date.setUTCDate(date.getUTCDate() + days);
    return formatDateOnly(date);
}
/**
 * Add whole months to a date-only string, clamping the day (Jan 31 + 1mo → Feb 28/29).
 *
 * Beside `addDays` rather than in `finance.ts`, where it was first written, so
 * the two date arithmetics a schedule can use sit together and nobody writes a
 * second month-add for want of finding this one. Recurring bills (#finance) and
 * definition-borne maintenance cadences (#1206) both call it, and they must
 * clamp identically — a template that says "every month" from the 31st has to
 * land where a bill due on the 31st lands. `finance.ts` re-exports it so every
 * existing importer is untouched.
 *
 * It shares `daysInMonth` with `monthDateFromStart` below, which is how the
 * chores engine itself walks a monthly recurrence — so a cadence seeded from a
 * template lands on the same day the engine would have generated anyway.
 */
export function addMonthsDateOnly(dateOnly, months) {
    const date = parseDateOnly(dateOnly);
    const day = date.getUTCDate();
    const absoluteMonth = date.getUTCFullYear() * 12 + date.getUTCMonth() + months;
    const year = Math.floor(absoluteMonth / 12);
    const monthIndex = ((absoluteMonth % 12) + 12) % 12;
    const clampedDay = Math.min(day, daysInMonth(year, monthIndex));
    return formatDateOnly(new Date(Date.UTC(year, monthIndex, clampedDay)));
}
export function compareDateOnly(left, right) {
    assertDateOnly(left);
    assertDateOnly(right);
    return left < right ? -1 : left > right ? 1 : 0;
}
function daysBetween(startDate, endDate) {
    const start = parseDateOnly(startDate).getTime();
    const end = parseDateOnly(endDate).getTime();
    return Math.floor((end - start) / 86_400_000);
}
function weekStart(dateOnly) {
    const date = parseDateOnly(dateOnly);
    date.setUTCDate(date.getUTCDate() - date.getUTCDay());
    return formatDateOnly(date);
}
function weeksBetween(startDate, endDate) {
    return Math.floor(daysBetween(weekStart(startDate), weekStart(endDate)) / 7);
}
function dayOfWeek(dateOnly) {
    return parseDateOnly(dateOnly).getUTCDay();
}
function daysInMonth(year, monthIndex) {
    return new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
}
function monthDateFromStart(startDate, monthOffset, dayOfMonth) {
    const start = parseDateOnly(startDate);
    const absoluteMonth = start.getUTCFullYear() * 12 + start.getUTCMonth() + monthOffset;
    const year = Math.floor(absoluteMonth / 12);
    const monthIndex = absoluteMonth % 12;
    const day = Math.min(dayOfMonth, daysInMonth(year, monthIndex));
    return formatDateOnly(new Date(Date.UTC(year, monthIndex, day)));
}
function recurrenceEnd(rule, throughDate) {
    const endDate = "endDate" in rule ? rule.endDate : "";
    return endDate && compareDateOnly(endDate, throughDate) < 0 ? endDate : throughDate;
}
export function generateOccurrenceDates(rule, startDate, throughDate) {
    assertDateOnly(startDate);
    assertDateOnly(throughDate);
    if (compareDateOnly(throughDate, startDate) < 0)
        return [];
    if (!rule)
        return [startDate];
    const end = recurrenceEnd(rule, throughDate);
    if (compareDateOnly(end, startDate) < 0)
        return [];
    const limitOccurrences = (dates) => {
        const count = "occurrenceCount" in rule ? rule.occurrenceCount : undefined;
        return count ? dates.slice(0, count) : dates;
    };
    if (rule.frequency === "daily") {
        const interval = rule.interval ?? 1;
        const dates = [];
        for (let offset = 0;; offset += interval) {
            const date = addDays(startDate, offset);
            if (compareDateOnly(date, end) > 0)
                break;
            dates.push(date);
        }
        return limitOccurrences(dates);
    }
    if (rule.frequency === "interval_days") {
        const dates = [];
        for (let offset = 0;; offset += rule.intervalDays) {
            const date = addDays(startDate, offset);
            if (compareDateOnly(date, end) > 0)
                break;
            dates.push(date);
        }
        return limitOccurrences(dates);
    }
    if (rule.frequency === "weekly") {
        const selectedWeekdays = new Set(rule.weekdays);
        const interval = rule.interval ?? 1;
        const dates = [];
        for (let date = startDate; compareDateOnly(date, end) <= 0; date = addDays(date, 1)) {
            if (!selectedWeekdays.has(dayOfWeek(date)))
                continue;
            if (weeksBetween(startDate, date) % interval !== 0)
                continue;
            dates.push(date);
        }
        return limitOccurrences(dates);
    }
    const interval = rule.interval ?? 1;
    const dates = [];
    for (let monthOffset = 0; monthOffset <= 1_200; monthOffset += interval) {
        const date = monthDateFromStart(startDate, monthOffset, rule.dayOfMonth);
        if (compareDateOnly(date, startDate) < 0)
            continue;
        if (compareDateOnly(date, end) > 0)
            break;
        dates.push(date);
    }
    return limitOccurrences(dates);
}
export function nextOccurrenceDate(rule, startDate, afterDate) {
    assertDateOnly(startDate);
    assertDateOnly(afterDate);
    if (!rule)
        return compareDateOnly(startDate, afterDate) > 0 ? startDate : null;
    if (rule.frequency === "daily" || rule.frequency === "interval_days") {
        const interval = rule.frequency === "daily" ? rule.interval ?? 1 : rule.intervalDays;
        const diff = daysBetween(startDate, afterDate);
        const nextOffset = diff < 0 ? 0 : (Math.floor(diff / interval) + 1) * interval;
        const candidate = addDays(startDate, nextOffset);
        return rule.endDate && compareDateOnly(candidate, rule.endDate) > 0 ? null : candidate;
    }
    const horizon = addDays(afterDate, 366 * 10);
    const dates = generateOccurrenceDates(rule, startDate, horizon);
    return dates.find((date) => compareDateOnly(date, afterDate) > 0) ?? null;
}
export function localDateInTimeZone(now, timezone = "UTC") {
    const date = typeof now === "string" ? new Date(now) : now;
    const safeTimezone = timezone || "UTC";
    try {
        const parts = new Intl.DateTimeFormat("en-US", {
            timeZone: safeTimezone,
            year: "numeric",
            month: "2-digit",
            day: "2-digit"
        }).formatToParts(date);
        const year = parts.find((part) => part.type === "year")?.value;
        const month = parts.find((part) => part.type === "month")?.value;
        const day = parts.find((part) => part.type === "day")?.value;
        if (year && month && day)
            return `${year}-${month}-${day}`;
    }
    catch {
        // Fall back to UTC for malformed or unsupported timezones.
    }
    return formatDateOnly(date);
}
/**
 * A strict ISO-8601 instant, which is what `nowIso()` writes.
 *
 * `Date.parse` is not this check: it accepts an expanded-year form
 * (`+010000-01-01T00:00:00.000Z`), so parsing alone let a value that was never
 * a real instant through as a confident wrong answer.
 *
 * The SHAPE is all this pattern claims, though — `2026-02-30T00:00:00.000Z`
 * matches it perfectly well. Calendar validity is a separate question and is
 * answered below.
 */
const isoInstantPattern = /^(\d{4})-(\d{2})-(\d{2})T\d{2}:\d{2}:\d{2}(\.\d{1,3})?(Z|[+-]\d{2}:\d{2})$/;
/**
 * Is this a day that actually exists?
 *
 * JavaScript does not answer this: `new Date(Date.UTC(2026, 1, 30))` ROLLS
 * forward to March 2 rather than refusing, which is why a shape check cannot
 * stand in for a calendar check. Building the date and reading the components
 * back is what catches it — a rolled date never round-trips.
 *
 * Deliberately asked of the date PART alone, independent of the offset: an
 * instant like `2026-08-30T22:00:00-04:00` is a perfectly real Aug 30 that
 * falls on Aug 31 in UTC, and round-tripping the whole instant would reject it.
 */
function isRealCalendarDate(year, month, day) {
    const probe = new Date(Date.UTC(year, month - 1, day));
    return probe.getUTCFullYear() === year
        && probe.getUTCMonth() + 1 === month
        && probe.getUTCDate() === day;
}
/**
 * The local date an occurrence was actually completed on.
 *
 * `completedAt` is only as trustworthy as what the completion route accepted,
 * and `completionRequestSchema` takes any trimmed string up to 80 characters —
 * so a stored value need not be a timestamp at all. `localDateInTimeZone`
 * throws `RangeError` on one that will not parse (its internal try/catch covers
 * a bad TIMEZONE, not a bad date), which would have turned every maintenance
 * read for that household into a permanent 500.
 *
 * Three checks, because each of three review rounds on #1205 found the previous
 * set was not enough:
 *
 * - the SHAPE must be an ISO instant, which is what `nowIso()` writes;
 * - the DAY must exist, because JavaScript rolls `2026-02-30` forward to
 *   March 2 instead of refusing it, and a rolled date is a wrong answer stated
 *   confidently rather than a crash somebody would notice;
 * - the CONVERTED value must be `YYYY-MM-DD`, because an expanded year parses
 *   happily and converts to `10000-01-01`, which `compareDateOnly` then rejects
 *   one sort later — a 500 moved rather than removed.
 *
 * Guarded here rather than by tightening the shared completion schema: that
 * schema is the whole app's chore-completion body, narrowing it is a behaviour
 * change well outside the item that found this, and it would do nothing about
 * rows already stored. A read that cannot trust its input is the one that has
 * to cope.
 *
 * Every rejected value falls back to the date the work was OWED, which is the
 * best answer available when the recorded one is not a date at all.
 *
 * Shared rather than private to the maintenance reader that first needed it
 * (#1205), because #1412's completion anchor asks the SAME question at the
 * write — "which day was this done on?" — and two answers to it would let a
 * schedule re-anchor onto a date its own service history disagrees with.
 */
export function completionDateOnly(row) {
    const match = isoInstantPattern.exec(row.completedAt);
    if (!match)
        return row.dueDate;
    if (!isRealCalendarDate(Number(match[1]), Number(match[2]), Number(match[3])))
        return row.dueDate;
    if (Number.isNaN(new Date(row.completedAt).getTime()))
        return row.dueDate;
    const converted = localDateInTimeZone(row.completedAt, row.timezone || "UTC");
    return dateOnlyPattern.test(converted) ? converted : row.dueDate;
}
/**
 * Where a repeating schedule's next occurrence falls when it repeats from the
 * work rather than from the calendar (#1412).
 *
 * The rule is re-anchored on the completion and asked for its first date after
 * it — which is the whole change. It is expressed with `nextOccurrenceDate`
 * rather than by adding an interval by hand for two reasons:
 *
 * - the answer it returns is always a date the rule itself generates FROM that
 *   answer, so the new anchor is a legal anchor. Every generation path in the
 *   engine derives its dates from `(rule, anchor)`, and an anchor the rule
 *   would not itself produce is one those paths would silently disagree with;
 * - a rule that names a calendar slot keeps naming it. "Every month on the
 *   15th" completed on the 20th is next owed the 15th of NEXT month, not the
 *   20th — the toggle moves which slot the count starts from, it does not
 *   rewrite the household's rule. Where it bites hardest is exactly where the
 *   issue says it should: `interval_days`, the shape "every 90 days" is stored
 *   as, lands a full interval after the work every time.
 *
 * `owedOn` — the date the completed occurrence was owed — is a FLOOR, not the
 * anchor. Work done EARLY would otherwise land the next one almost on top of
 * it: a monthly schedule due on the 15th and done on the 10th re-anchors to the
 * 10th, whose own next slot is the 15th of that same month, five days later.
 * The floor says the obvious thing instead — the next one comes after the one
 * just finished, whenever the household got to it — and costs nothing in the
 * late case, where the completion is already the later of the two.
 *
 * `null` when the rule has run out — an end date already passed — and the
 * caller leaves the anchor alone, because there is no next occurrence to
 * anchor.
 */
export function completionAnchoredNextDate(rule, completedOn, owedOn) {
    if (!rule)
        return null;
    const after = compareDateOnly(completedOn, owedOn) > 0 ? completedOn : owedOn;
    return nextOccurrenceDate(rule, completedOn, after);
}
export function effectiveOccurrenceDueDate(occurrence) {
    return occurrence.status === "snoozed" && occurrence.snoozedUntil ? occurrence.snoozedUntil : occurrence.dueDate;
}
export function occurrenceTemporalState(occurrence, now = new Date()) {
    const today = localDateInTimeZone(now, occurrence.timezone || "UTC");
    const effectiveDueDate = effectiveOccurrenceDueDate(occurrence);
    if (compareDateOnly(effectiveDueDate, today) < 0)
        return "overdue";
    if (effectiveDueDate === today)
        return "today";
    return "upcoming";
}
export function isOccurrenceDueToday(occurrence, now = new Date()) {
    return occurrenceTemporalState(occurrence, now) === "today";
}
export function isOccurrenceOverdue(occurrence, now = new Date()) {
    return occurrenceTemporalState(occurrence, now) === "overdue";
}
